import React from 'react';
import ArticleShell from './ArticleShell';
import { H2, H3, P, Callout, CheckList, BulletList } from './academyPrimitives';
import { getGuideBySlug } from '../../data/knowledgeAcademy';

const UAEFreeZonesDesignatedZonesGuide = () => {
  const guide = {
    ...getGuideBySlug('uae-free-zones-vs-designated-zones-tax'),
    relatedService: { label: 'Corporate Tax Services', path: '/corporate-tax-uae' }
  };

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'at-a-glance', label: 'Three Classifications at a Glance' },
    { id: 'ct-free-zone', label: '1. Free Zones for Corporate Tax' },
    { id: 'ct-designated', label: '2. Corporate Tax Designated Zones' },
    { id: 'vat-designated', label: '3. VAT Designated Zones' },
    { id: 'excise-designated', label: '4. Excise Tax Designated Zones' },
    { id: 'examples', label: 'Practical Examples' },
    { id: 'checklist', label: 'Compliance Checklist' },
    { id: 'faq', label: 'Frequently Asked Questions' },
    { id: 'wpaa-help', label: 'How WPAA Can Assist' }
  ];

  return (
    <ArticleShell guide={guide} toc={toc} testId="ka-article-fz-dz" ctaService="corporate-tax">
      <Callout variant="info" title="At a glance">
        A location may qualify under one tax regime but not under another. <strong>Never apply a Free Zone
        or Designated Zone conclusion across Corporate Tax, VAT and Excise Tax without a separate review.</strong>
      </Callout>

      <H2 id="overview">Overview</H2>
      <P>
        FTA <strong>Public Clarification TAXP010</strong> makes one point especially clear: the expressions
        &ldquo;Free Zone&rdquo; and &ldquo;Designated Zone&rdquo; do not have the same meaning across the
        UAE Corporate Tax, VAT and Excise Tax regimes. A business must establish its status
        <strong> separately under each tax law</strong>.
      </P>
      <P>
        The UAE Federal Tax Authority (FTA) issued Public Clarification TAXP010,
        <em> Free Zones and Designated Zones for Tax Purposes</em>, on 2 October 2026. The clarification
        helps businesses determine whether their location is recognised as:
      </P>
      <BulletList items={[
        'A Free Zone for Corporate Tax purposes',
        'A Designated Zone for Corporate Tax purposes',
        'A Designated Zone for VAT purposes, or',
        'A Designated Zone for Excise Tax purposes'
      ]} />
      <P>
        This classification is not merely a matter of terminology. It may directly affect a company&apos;s
        Corporate Tax treatment, VAT analysis, Excise Tax controls, documentation and compliance obligations.
      </P>

      <H2 id="at-a-glance">The Three Tax Classifications at a Glance</H2>
      <div className="my-8 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-teal-50 text-teal-900">
            <tr>
              <th className="text-left font-semibold uppercase tracking-wider text-[11px] px-4 py-3 border-b border-teal-100">Tax Regime</th>
              <th className="text-left font-semibold uppercase tracking-wider text-[11px] px-4 py-3 border-b border-teal-100">What the Zone Means</th>
              <th className="text-left font-semibold uppercase tracking-wider text-[11px] px-4 py-3 border-b border-teal-100">How to Verify Status</th>
              <th className="text-left font-semibold uppercase tracking-wider text-[11px] px-4 py-3 border-b border-teal-100">Why It Matters</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr className="bg-white align-top">
              <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">Corporate Tax — Free Zone</td>
              <td className="px-4 py-3 text-gray-700">A designated geographic area in the UAE specified by Cabinet decision at the Minister&apos;s suggestion</td>
              <td className="px-4 py-3 text-gray-700">Confirm with the relevant Free Zone authority</td>
              <td className="px-4 py-3 text-gray-700">A juridical person established in such a zone may be a Free Zone Person — but the 0% rate is <strong>not automatic</strong></td>
            </tr>
            <tr className="bg-gray-50/60 align-top">
              <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">Corporate Tax — Designated Zone</td>
              <td className="px-4 py-3 text-gray-700">A zone that is <strong>both</strong> a VAT Designated Zone <strong>and</strong> a Free Zone for Corporate Tax</td>
              <td className="px-4 py-3 text-gray-700">Confirm with the Free Zone authority and verify VAT designation</td>
              <td className="px-4 py-3 text-gray-700">Particularly important for the qualifying activity of distributing goods in or from a Designated Zone</td>
            </tr>
            <tr className="bg-white align-top">
              <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">VAT — Designated Zone</td>
              <td className="px-4 py-3 text-gray-700">A listed Free Zone that also satisfies the conditions in Article 51(1) of the VAT Executive Regulation</td>
              <td className="px-4 py-3 text-gray-700">Review the FTA&apos;s current List of Designated Zones; confirm conditions with the Free Zone authority</td>
              <td className="px-4 py-3 text-gray-700">Certain transactions involving goods may receive special VAT treatment, subject to conditions</td>
            </tr>
            <tr className="bg-gray-50/60 align-top">
              <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">Excise Tax — Designated Zone</td>
              <td className="px-4 py-3 text-gray-700">A qualifying fenced Free Zone or another approved geographic area meeting Excise Tax conditions</td>
              <td className="px-4 py-3 text-gray-700">Confirm with the Free Zone authority or Warehouse Keeper that the FTA has approved the area</td>
              <td className="px-4 py-3 text-gray-700">Controls treatment, storage and movement of Excise Goods under an approved Warehouse Keeper</td>
            </tr>
          </tbody>
        </table>
      </div>

      <H2 id="ct-free-zone">1. Free Zones for Corporate Tax Purposes</H2>
      <P>
        For Corporate Tax, a Free Zone is a designated and defined geographic area within the UAE that is
        specified by a Cabinet decision issued at the suggestion of the Minister.
      </P>
      <P>
        A juridical person incorporated, established or otherwise registered in a Corporate Tax Free Zone
        may fall within the definition of a <strong>Free Zone Person</strong>. This includes a branch of a
        non-resident person registered in a Free Zone.
      </P>
      <Callout variant="warning" title="Being in a Free Zone does not mean 0%">
        The entity must meet all the conditions required to be treated as a <strong>Qualifying Free Zone
        Person (QFZP)</strong>, and the 0% rate applies only to its <strong>Qualifying Income</strong>.
      </Callout>
      <P>The business must therefore consider matters such as:</P>
      <CheckList items={[
        'Whether it is incorporated or registered in a recognised Corporate Tax Free Zone',
        'The nature of each income stream',
        'Whether transactions are with Free Zone Persons or Non-Free Zone Persons',
        'Whether the counterparty is the beneficial recipient, where relevant',
        'Whether the activity is a Qualifying Activity or an Excluded Activity',
        'Whether the de minimis, substance, transfer pricing and other QFZP conditions are satisfied',
        'Whether the income is attributable to a domestic or foreign permanent establishment or relates to immovable property'
      ]} />

      <H2 id="ct-designated">2. Corporate Tax Designated Zones: The Additional Test</H2>
      <P>
        A Corporate Tax Designated Zone is not simply any Free Zone. Under the relevant Corporate Tax
        rules, the location must satisfy <strong>both</strong> of the following tests:
      </P>
      <CheckList items={[
        'It is a Designated Zone under the VAT legislation',
        'It is included as a Free Zone for Corporate Tax purposes'
      ]} />
      <P>
        This means that a VAT Designated Zone is <strong>not automatically</strong> a Corporate Tax
        Designated Zone.
      </P>

      <H3>Why Distributors Need Special Attention</H3>
      <P>
        <strong>Ministerial Decision No. 229 of 2025</strong> recognises the distribution of goods or
        materials in or from a Corporate Tax Designated Zone as a Qualifying Activity, subject to the
        applicable conditions. The activity may include:
      </P>
      <BulletList items={[
        'Buying and selling tangible or movable goods, materials, component parts or other items',
        'Importing, storing or managing inventory',
        'Handling, transporting or exporting those goods or materials',
        'Related distribution functions carried out in or from the Designated Zone'
      ]} />
      <P>
        Among the conditions described in the clarification, the relevant activities must be conducted in
        or from the Designated Zone, goods entering the UAE must be imported through the Designated Zone,
        and the goods or materials must be supplied to:
      </P>
      <BulletList items={[
        'A customer that resells, processes or alters them for sale or resale, or',
        'A public benefit entity'
      ]} />
      <P>
        The complete QFZP and distribution requirements must still be assessed. Incorporation in an
        ordinary Free Zone, by itself, is <strong>not enough</strong> to treat distribution income as
        Qualifying Income under this category.
      </P>

      <H3>Consequence of Losing QFZP Status</H3>
      <Callout variant="danger" title="The penalty can extend five years">
        Where a QFZP fails to meet the applicable conditions at any time during a Tax Period, it may cease
        to be a QFZP from the <strong>beginning of that Tax Period</strong> and for the
        <strong> following four Tax Periods</strong>. Review eligibility before every return and monitor
        the conditions throughout each Tax Period.
      </Callout>

      <H2 id="vat-designated">3. Designated Zones for VAT Purposes</H2>
      <P>
        A VAT Designated Zone is a specific fenced geographic area included in the official List of
        Designated Zones under <strong>Cabinet Decision No. 59 of 2017</strong> and its amendments. To be
        treated as outside the UAE for the relevant VAT rules, the zone must also satisfy the conditions
        in <strong>Article 51(1) of the VAT Executive Regulation</strong>:
      </P>
      <CheckList items={[
        'A specific fenced geographic area',
        'Security measures and customs controls monitoring the entry and exit of people and the movement of goods',
        'Internal procedures governing the keeping, storage and processing of goods',
        'Compliance by the zone operator with procedures prescribed by the FTA'
      ]} />

      <H3>A VAT Designated Zone Is Not a Blanket &ldquo;No VAT&rdquo; Area</H3>
      <P>
        The special VAT treatment is <strong>conditional and transaction-specific</strong>. A business
        should not assume that every supply made from, received in or connected with a VAT Designated Zone
        is outside the scope of UAE VAT.
      </P>
      <P>The correct VAT treatment can depend on factors including:</P>
      <BulletList items={[
        'Whether the transaction involves goods or services',
        'Where the goods are located before and after the supply',
        'Whether the goods are consumed within the Designated Zone',
        'Whether the goods move to the UAE mainland or outside the UAE',
        'Who imports the goods and whether import VAT is accounted for',
        'Whether the required commercial, customs and movement evidence is retained'
      ]} />
      <P>
        The business should review the current FTA list and confirm with the relevant Free Zone authority
        that the specific area in which it operates continues to satisfy the required conditions.
      </P>

      <H2 id="excise-designated">4. Designated Zones for Excise Tax Purposes</H2>
      <P>For Excise Tax, a Designated Zone may be:</P>
      <BulletList items={[
        'A fenced Free Zone that can be entered or exited only through a designated route and that meets the prescribed conditions, or',
        'Another specific geographic area designated by the FTA and supervised by a Warehouse Keeper'
      ]} />
      <P>
        For a fenced Free Zone to qualify, it must have appropriate security measures controlling the
        movement of people and Excise Goods, be controlled and supervised by a customs department, and
        have an appointed Warehouse Keeper.
      </P>
      <P>
        For another geographic area to qualify, it must be a specific area with the required security
        controls and an appointed Warehouse Keeper. The Warehouse Keeper must apply to the FTA to register
        the area as a Designated Zone.
      </P>
      <Callout variant="warning" title="Storing Excise Goods in a Free Zone is not enough">
        The fact that Excise Goods are stored in a Free Zone warehouse does not, on its own, establish
        that the warehouse is located in an Excise Tax Designated Zone. Obtain written confirmation from
        the Free Zone authority or Warehouse Keeper that the FTA has approved the particular area.
      </Callout>

      <H2 id="examples">Practical Examples</H2>

      <H3>Example 1: Distributor Established in a Free Zone</H3>
      <P>
        A trading company is incorporated in a Corporate Tax Free Zone but carries out its distribution
        operations from an area that is <strong>not</strong> a Corporate Tax Designated Zone.
      </P>
      <Callout variant="danger" title="0% not automatic">
        The company may be a Free Zone Person, but it cannot assume that its distribution income qualifies
        for the 0% Corporate Tax rate under the Designated Zone distribution category. Its status,
        activity, location, customer profile and all other QFZP conditions require separate analysis.
      </Callout>

      <H3>Example 2: Goods Stored in a VAT Designated Zone</H3>
      <P>
        A company stores imported goods in a listed VAT Designated Zone and later sells some goods into
        the UAE mainland.
      </P>
      <Callout variant="warning" title="Transaction-specific analysis required">
        Do not treat every transaction as outside the scope of VAT. Analyse the movement and use of the
        goods, the importer of record, the relevant VAT point and the supporting customs documentation.
      </Callout>

      <H3>Example 3: Excise Goods Held in a Free Zone Warehouse</H3>
      <P>
        A business stores tobacco products, electronic smoking devices, sweetened drinks or other Excise
        Goods in a Free Zone warehouse.
      </P>
      <Callout variant="danger" title="Approval must be verified">
        The physical location in a Free Zone does not prove that Excise Tax Designated Zone treatment
        applies. Approval of the specific area, Warehouse Keeper supervision and the prescribed security
        and movement controls must be verified.
      </Callout>

      <H2 id="checklist">A Practical Compliance Checklist</H2>
      <CheckList items={[
        'Identify the exact operating location — Free Zone, plot, warehouse, office and branch details, not just the trade licence address',
        'Test each tax separately — prepare independent conclusions for Corporate Tax, VAT and Excise Tax',
        'Obtain written confirmation from the relevant Free Zone authority for Corporate Tax and VAT purposes',
        "Check the FTA's latest List of Designated Zones for VAT",
        'Verify Excise approval with the Warehouse Keeper or Free Zone authority where Excise Goods are involved',
        'Map transactions and income streams — goods, services, customers, suppliers, beneficial recipients, import routes, delivery terms and end use',
        'Maintain evidence — contracts, tax invoices, customs declarations, transport records, inventory records and proof of movement or consumption',
        'Review the conclusion regularly — zone status, legislation and operational arrangements can change; reassess for each Tax Period before filing'
      ]} />

      <H2 id="faq">Frequently Asked Questions</H2>

      <H3>Are all UAE Free Zones also VAT Designated Zones?</H3>
      <P>
        No. Only zones included in the official list and satisfying the relevant statutory conditions
        qualify as VAT Designated Zones.
      </P>

      <H3>Is every VAT Designated Zone a Corporate Tax Designated Zone?</H3>
      <P>
        No. For Corporate Tax purposes, the zone must be both a VAT Designated Zone and a recognised
        Corporate Tax Free Zone.
      </P>

      <H3>Does a Free Zone company automatically receive 0% Corporate Tax?</H3>
      <P>
        No. The 0% rate is available to a QFZP only on Qualifying Income and only while all relevant
        conditions are satisfied.
      </P>

      <H3>Does &ldquo;Designated Zone&rdquo; mean that VAT never applies?</H3>
      <P>
        No. The VAT treatment depends on the nature and facts of the transaction and on satisfaction of
        the applicable conditions. Designated Zone status is not a general VAT exemption.
      </P>

      <H3>Who should confirm the status of the zone?</H3>
      <P>
        For Corporate Tax, the FTA clarification directs taxpayers to the relevant Free Zone authority.
        For VAT, businesses should check the official FTA list and confirm the statutory conditions with
        the Free Zone authority. For Excise Tax, confirmation should be obtained from the Free Zone
        authority or Warehouse Keeper, as applicable.
      </P>

      <H2 id="wpaa-help">How WPAA Can Assist</H2>
      <P>White Paper Accounts Auditing (WPAA) can support UAE businesses with:</P>
      <CheckList items={[
        'Free Zone and Designated Zone classification reviews',
        'QFZP eligibility and Qualifying Income assessments',
        'Transaction-by-transaction Corporate Tax and VAT analysis',
        'Distribution activity and customer eligibility reviews',
        'Documentation and compliance checklists',
        'Corporate Tax return and VAT return support',
        'Tax risk reviews before an audit, restructuring or expansion'
      ]} />
      <Callout variant="info" title="Tailored advice">
        For advice tailored to your business, contact the WPAA Tax &amp; Advisory Team.
      </Callout>

      <H2>Official References</H2>
      <BulletList items={[
        'UAE Federal Tax Authority — Free Zones and Designated Zones for Tax Purposes (TAXP010), issued 2 October 2026',
        'UAE Federal Tax Authority — Guides, References & Public Clarifications',
        'UAE Federal Tax Authority — List of Designated Zones for VAT purposes'
      ]} />
      <P className="text-xs text-gray-500 italic mt-6">Last reviewed: 10 October 2026.</P>

      <Callout variant="warning" title="Disclaimer">
        This article is intended for general information and educational purposes only. It does not
        constitute legal, tax or professional advice. Tax treatment depends on the legislation in force
        and the specific facts of each business and transaction. Businesses should obtain professional
        advice before taking or refraining from any action.
      </Callout>
    </ArticleShell>
  );
};

export default UAEFreeZonesDesignatedZonesGuide;
